// components/Sender/SenderSegmentsTab.js
import React, { useState, useEffect } from "react";
import { SenderApi } from "./SenderApi";
import { ImportJsonDialog } from "./ImportJsonDialog";

const EMAIL_STATUS = [
  ["verified",   "✅ Верифицирован"],
  ["invalid",    "❌ Невалидный"],
  ["exhausted",  "🛑 Исчерпан"],
  ["transient",  "⏳ Transient"],
  ["unverified", "❔ Не проверен"],
];
const YN = [["yes", "✅ Есть"], ["no", "❌ Нет"]];
const DOUBLED = [["doubled", "✅ Сдвоенные"], ["single", "❌ Одиночные"]];

export function SenderSegmentsTab({ segments, setSegments, add }) {
  const [editing, setEditing] = useState(null);
  const [campaigns, setCampaigns] = useState({});
  const [importOpen, setImportOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [blocked, setBlocked] = useState(null);   // [{id, name, templates}]
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    SenderApi.listCampaigns()
      .then((list) => {
        const arr = Array.isArray(list) ? list : (list?.results || []);
        const map = {};
        arr.forEach((c) => { map[String(c.id)] = c.name || `#${c.id}`; });
        setCampaigns(map);
      })
      .catch((e) => {
        console.error("[SenderSegmentsTab] campaigns load failed:", e);
        add?.("⚠️ Не удалось получить кампании: " + e.message, "warn");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    try {
      const list = await SenderApi.listSegments();
      setSegments(list || []);
    } catch (e) {
      add?.("Не удалось обновить сегменты: " + e.message, "error");
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === segments.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(segments.map((s) => s.id)));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const applyBulkResult = (res) => {
    const deleted = new Set(res.deleted || []);
    if (deleted.size) {
      setSegments(segments.filter((s) => !deleted.has(s.id)));
    }
    const stillBlocked = (res.blocked || []).length > 0;
    if (stillBlocked) {
      setBlocked(res.blocked);
    } else {
      setBlocked(null);
    }
    return deleted.size;
  };

  const bulkDelete = async (idsOverride) => {
    const ids = idsOverride || Array.from(selectedIds);
    if (!ids.length) return;
    setBusy(true);
    try {
      const res = await SenderApi.bulkDeleteSegments(ids, /* force */ false);
      const n = applyBulkResult(res);
      if (n > 0) {
        add?.(`🗑 Удалено сегментов: ${n}`, "success");
        setSelectedIds(new Set());
      }
      // если есть blocked — откроется диалог
    } catch (e) {
      add?.("Ошибка удаления: " + e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const bulkForceDelete = async () => {
    if (!blocked) return;
    const ids = blocked.map((b) => b.id);
    setBusy(true);
    try {
      const res = await SenderApi.bulkDeleteSegments(ids, /* force */ true);
      const n = applyBulkResult(res);
      add?.(
        `🗑 Удалено сегментов: ${n} (использование в шаблонах тоже удалено)`,
        "success"
      );
      setBlocked(null);
      setSelectedIds(new Set());
    } catch (e) {
      add?.("Ошибка удаления: " + e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const deleteOne = async (s) => {
    if (!window.confirm(`Удалить сегмент «${s.name}»?`)) return;
    await bulkDelete([s.id]);
  };

  const onSave = async (s) => {
    try {
      const payload = { name: s.name, filters: s.filters };
      if (s.id) {
        const updated = await SenderApi.updateSegment({ ...payload, id: s.id });
        setSegments(segments.map((x) => (x.id === s.id ? updated : x)));
      } else {
        const created = await SenderApi.createSegment(payload);
        setSegments([...segments, created]);
      }
      setEditing(null);
      add?.("💾 Сегмент сохранён", "success");
    } catch (e) {
      add?.("Ошибка: " + e.message, "error");
    }
  };

  const selectedCount = selectedIds.size;
  const allSelected = selectedCount > 0 && selectedCount === segments.length;

  return (
    <div>
      <div className="section-card" style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div>
            <h3 style={{ margin: 0 }}>
              🗂 Сегменты
              {selectedCount > 0 && (
                <span style={{ fontSize: 13, color: "#dc2626",
                                fontWeight: 400, marginLeft: 10 }}>
                  · выбрано {selectedCount}
                </span>
              )}
            </h3>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
              Всего: {segments.length} · хранятся на глобальном бекенде
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {selectedCount > 0 ? (
              <>
                <button className="btn-cancel" onClick={clearSelection}>
                  Снять выбор
                </button>
                <button className="btn-cancel" disabled={busy}
                        onClick={() => bulkDelete()}
                        style={{ color: "#dc2626", borderColor: "#fecaca" }}>
                  🗑 Удалить выбранные ({selectedCount})
                </button>
              </>
            ) : (
              <>
                <button className="btn-cancel" onClick={refresh}>🔄 Обновить</button>
                <button className="btn-cancel"
                        onClick={() => setImportOpen(true)}>
                  📥 Импорт
                </button>
                <button className="btn-primary"
                        onClick={() => setEditing({ segment: newSegment() })}>
                  ➕ Новый сегмент
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="section-card">
        {segments.length === 0 ? (
          <p style={{ color: "#9ca3af", textAlign: "center", padding: 30 }}>
            Пока нет сегментов.
          </p>
        ) : (
          <>
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "8px 12px", background: "#f9fafb",
              borderRadius: 8, marginBottom: 10, fontSize: 12,
            }}>
              <input type="checkbox" checked={allSelected}
                     onChange={toggleSelectAll} />
              <span style={{ color: "#6b7280" }}>
                {allSelected ? "Снять выбор со всех" : "Выбрать все"}
              </span>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {segments.map((s) => {
                const f = s.filters || {};
                const isSelected = selectedIds.has(s.id);
                return (
                  <div key={s.id}
                       style={{
                         border: `1px solid ${isSelected ? "#93c5fd" : "#e5e7eb"}`,
                         background: isSelected ? "#eff6ff" : "#fff",
                         borderRadius: 10, padding: 14,
                         display: "flex", alignItems: "flex-start", gap: 10,
                       }}>
                    <input type="checkbox"
                           checked={isSelected}
                           onChange={() => toggleSelect(s.id)}
                           style={{ marginTop: 4 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>
                        {s.name}
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280",
                                    display: "flex", flexWrap: "wrap",
                                    columnGap: 12, rowGap: 4 }}>
                        {f.campaign_id?.length > 0 && (
                          <span>📢 {f.campaign_id.map((id) =>
                            campaigns[id] || `#${id}`).join(", ")}</span>
                        )}
                        {f.email_status?.length > 0 && (
                          <span>📧 {f.email_status.map((v) =>
                            EMAIL_STATUS.find(([k]) => k === v)?.[1] || v)
                            .join(", ")}</span>
                        )}
                        {f.provider?.length > 0 && (
                          <span>🏢 {f.provider.join(", ")}</span>
                        )}
                        {f.contact_form?.length > 0 && (
                          <span>🔗 {f.contact_form.map((v) =>
                            YN.find(([k]) => k === v)?.[1] || v).join(", ")}</span>
                        )}
                        {f.doubled?.length > 0 && (
                          <span>🎯 {f.doubled.map((v) =>
                            DOUBLED.find(([k]) => k === v)?.[1] || v)
                            .join(", ")}</span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button className="btn-cancel"
                              style={{ padding: "4px 10px", fontSize: 12 }}
                              onClick={() => setEditing({ segment: s })}>✏️</button>
                      <button className="btn-cancel"
                              style={{ padding: "4px 10px", fontSize: 12,
                                       color: "#dc2626" }}
                              onClick={() => deleteOne(s)}>🗑</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {editing && (
        <SegmentDialog
          initial={editing.segment}
          campaigns={campaigns}
          onSave={onSave}
          onCancel={() => setEditing(null)}
        />
      )}

      {importOpen && (
        <ImportJsonDialog
          kind="segments"
          add={add}
          onClose={() => setImportOpen(false)}
          onImported={refresh}
        />
      )}

      {blocked && (
        <BlockedDialog
          blocked={blocked}
          busy={busy}
          onForce={bulkForceDelete}
          onCancel={() => setBlocked(null)}
        />
      )}
    </div>
  );
}

// ─── Диалог «используется в шаблонах» для bulk ─────────────
function BlockedDialog({ blocked, busy, onForce, onCancel }) {
  return (
    <div style={overlay} onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ ...card, maxWidth: 620 }}>
        <div style={header}>
          <h3 style={{ margin: 0 }}>⚠️ Сегменты используются</h3>
          <button className="btn-cancel" onClick={onCancel}>✕</button>
        </div>

        <div style={{ padding: 20, fontSize: 13, lineHeight: 1.6 }}>
          Следующие сегменты используются в шаблонах:
          <div style={{
            marginTop: 10, maxHeight: 300, overflowY: "auto",
            background: "#f9fafb", borderRadius: 8, padding: 12,
          }}>
            {blocked.map((b) => (
              <div key={b.id} style={{ marginBottom: 10 }}>
                <div style={{ fontWeight: 700 }}>«{b.name}»</div>
                <div style={{ fontSize: 12, color: "#6b7280",
                              marginTop: 2 }}>
                  в шаблонах:{" "}
                  {b.templates.map((t) => (
                    <span key={t.id}>
                      <b>{t.name}</b>{" "}
                      <code style={{ fontSize: 10 }}>{t.id}</code>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div style={{
            padding: 12, background: "#fef3c7", borderRadius: 8,
            color: "#92400e", fontSize: 12, marginTop: 12,
          }}>
            ⚠️ При «Удалить всё равно» <b>все блоки</b> этих шаблонов,
            использующие данные сегменты, будут удалены.
            Сами шаблоны останутся. Воркеры этих блоков тоже удалятся.
          </div>
        </div>

        <div style={footer}>
          <button className="btn-cancel" onClick={onCancel}>Отмена</button>
          <button className="btn-primary" disabled={busy}
                  style={{ background: "#dc2626", borderColor: "#dc2626" }}
                  onClick={onForce}>
            {busy ? "⏳ Удаление…" : "🗑 Удалить всё равно"}
          </button>
        </div>
      </div>
    </div>
  );
}

function newSegment() {
  return {
    id: "",
    name: "",
    filters: {
      campaign_id: [], email_status: [], provider: [],
      contact_form: [], facebook_page: [], calendly: [], doubled: [],
    },
  };
}

// SegmentDialog — без изменений (уже есть)
function SegmentDialog({ initial, campaigns, onSave, onCancel }) {
  const [seg, setSeg] = useState(() => ({
    ...initial,
    filters: { ...newSegment().filters, ...(initial.filters || {}) },
  }));
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const runPreview = async () => {
    setPreviewLoading(true);
    try {
      const data = await SenderApi.previewSegment(seg.filters);
      if (data == null) {
        setPreview({ error: "Сервер вернул пустой ответ." });
      } else if (typeof data !== "object" || !data.counts) {
        setPreview({
          error: "Ответ без поля counts. Ключи: " +
                 JSON.stringify(Object.keys(data)),
        });
      } else {
        setPreview(data);
      }
    } catch (e) {
      setPreview({ error: e.message });
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => { runPreview(); /* eslint-disable-next-line */ }, []);

  const patchFilter = (key, value) =>
    setSeg({ ...seg, filters: { ...seg.filters, [key]: value } });

  const counts = preview?.counts || {};
  const providerCounts = preview?.provider_counts || {};
  const emailStatusCounts = preview?.email_status_counts || {};
  const cfCounts = preview?.contact_form_counts || { yes: 0, no: 0 };

  const campaignsFromPreview = Array.isArray(preview?.campaigns)
    ? preview.campaigns : [];
  const campaignItems = (() => {
    const ids = new Set([
      ...Object.keys(campaigns || {}),
      ...campaignsFromPreview.map((c) => String(c.id)),
    ]);
    return Array.from(ids).sort().map((id) => {
      const name = campaigns[id]
        || campaignsFromPreview.find((c) => String(c.id) === id)?.name
        || `#${id}`;
      const cnt = campaignsFromPreview.find(
        (c) => String(c.id) === id)?.count ?? 0;
      return [`${name} · ${cnt}`, id];
    });
  })();

  return (
    <div style={overlay} onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} style={card}>
        <div style={header}>
          <h3 style={{ margin: 0 }}>🗂 Редактор сегмента</h3>
          <button className="btn-cancel" onClick={onCancel}>✕</button>
        </div>

        <div style={body}>
          <div>
            <label className="form-label">Название</label>
            <input className="form-input" value={seg.name}
                   onChange={(e) => setSeg({ ...seg, name: e.target.value })}
                   placeholder="Например: Gmail верифицированные" />
          </div>

          <div style={{ display: "grid",
                        gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <MultiSelect label="📢 Кампания" emptyText="Все кампании"
              items={campaignItems} value={seg.filters.campaign_id}
              onChange={(v) => patchFilter("campaign_id", v)} />
            <MultiSelect label="🎯 Сдвоенные" emptyText="Не важно"
              items={DOUBLED} value={seg.filters.doubled}
              onChange={(v) => patchFilter("doubled", v)} />
            <MultiSelect label="📧 Email-статус" emptyText="Любой"
              items={EMAIL_STATUS.map(([k, lab]) =>
                [`${lab} · ${emailStatusCounts[k] ?? 0}`, k])}
              value={seg.filters.email_status}
              onChange={(v) => patchFilter("email_status", v)} />
            <MultiSelect label="🏢 Провайдер" emptyText="Все провайдеры"
              items={Object.entries(providerCounts).map(
                ([name, cnt]) => [`${name} · ${cnt}`, name])}
              value={seg.filters.provider}
              onChange={(v) => patchFilter("provider", v)} />
            <MultiSelect label="🔗 Контакт-форма" emptyText="Не важно"
              items={[
                [`✅ Есть · ${cfCounts.yes ?? 0}`, "yes"],
                [`❌ Нет · ${cfCounts.no ?? 0}`, "no"],
              ]}
              value={seg.filters.contact_form}
              onChange={(v) => patchFilter("contact_form", v)} />
            <MultiSelect label="📘 Facebook" emptyText="Не важно"
              items={YN} value={seg.filters.facebook_page}
              onChange={(v) => patchFilter("facebook_page", v)} />
            <MultiSelect label="📅 Calendly" emptyText="Не важно"
              items={YN} value={seg.filters.calendly}
              onChange={(v) => patchFilter("calendly", v)} />
          </div>

          <div style={{
            background: "#eef2ff", borderRadius: 10,
            padding: "10px 14px", color: "#3730a3", fontSize: 13,
          }}>
            🎯 Найдено:{" "}
            {previewLoading ? "⏳" : (
              <b style={{ fontSize: 18 }}>{counts.total ?? 0}</b>
            )}
            {preview?.counts?.raw_total != null &&
              preview.counts.raw_total !== counts.total && (
                <span style={{ color: "#6b7280" }}>
                  {" "}(интеншенов: {preview.counts.raw_total})
                </span>
              )}
            {preview?.error && (
              <span style={{ color: "#dc2626" }}> · ⚠️ {preview.error}</span>
            )}
            <button className="btn-cancel"
                    style={{ marginLeft: 12, fontSize: 12, padding: "4px 10px" }}
                    onClick={runPreview}>
              🔄 Обновить счётчик
            </button>
          </div>
        </div>

        <div style={footer}>
          <button className="btn-cancel" onClick={onCancel}>Отмена</button>
          <button className="btn-primary"
                  onClick={() => {
                    if (!seg.name?.trim()) { alert("Укажи название"); return; }
                    onSave(seg);
                  }}>
            💾 Сохранить
          </button>
        </div>
      </div>
    </div>
  );
}

function MultiSelect({ label, emptyText, items, value, onChange }) {
  const val = value || [];
  const toggle = (v) => {
    if (val.includes(v)) onChange(val.filter((x) => x !== v));
    else onChange([...val, v]);
  };
  return (
    <div>
      <label className="form-label">{label}</label>
      <div style={{
        border: "1px solid #d1d5db", borderRadius: 8,
        padding: "8px 10px", maxHeight: 170, overflowY: "auto",
        background: "#fff",
      }}>
        {val.length === 0 && (
          <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 4 }}>
            {emptyText}
          </div>
        )}
        {items.map(([lab, v]) => (
          <label key={v}
                 style={{
                   display: "flex", gap: 8, padding: "3px 4px",
                   cursor: "pointer", fontSize: 12, borderRadius: 4,
                 }}>
            <input type="checkbox"
                   checked={val.includes(v)}
                   onChange={() => toggle(v)} />
            <span>{lab}</span>
          </label>
        ))}
        {items.length === 0 && (
          <div style={{ fontSize: 11, color: "#9ca3af" }}>— нет опций —</div>
        )}
      </div>
    </div>
  );
}

const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,.6)",
  backdropFilter: "blur(3px)", zIndex: 1200,
  display: "flex", alignItems: "center", justifyContent: "center",
  padding: 20 };
const card = { background: "#fff", borderRadius: 14, width: "100%",
  maxWidth: 980, maxHeight: "92vh", display: "flex",
  flexDirection: "column", overflow: "hidden" };
const header = { padding: "16px 20px", borderBottom: "1.5px solid #e5e7eb",
  display: "flex", justifyContent: "space-between", alignItems: "center" };
const body = { flex: 1, overflow: "auto", padding: 20,
  display: "flex", flexDirection: "column", gap: 16 };
const footer = { padding: "14px 20px", borderTop: "1.5px solid #e5e7eb",
  display: "flex", justifyContent: "flex-end", gap: 8 };